import {CsvModel} from "../../util/CSVWriteUtil";
import {Employee, EmployeeRef, OfficeVisitSheetRow} from "../../api/Model";
import {isoDate, isWeekend, month, WeekDay, withLastMonthDay} from "../../util/DateUtil";
import {hibobEmployeeUrl} from "../../components/employee-link/EmployeeLink";
import {EmployeeMap} from "../../api/EmployeeMapStore";
import {emptyEmployee} from "../../api/Empty";
import {
    EmployeeVisitSheet,
    EmployeeVisitType,
    isEmployeeVisit,
} from "../../api/EmployeeVisitStore";


function reportMonths(sheet: EmployeeVisitSheet): {
    month: string,
    firstDay: Date,
    lastDay: Date,
    fromStart: boolean,
    toEnd: boolean,
}[] {
    const months = new Map<string, { firstDay: Date, lastDay: Date }>();
    sheet.dates.forEach(date => {
        const d = new Date(date);
        const m = month(d)
        const prev = months.get(m)
        months.set(m, {
            firstDay: !prev || d.getDate() < prev.firstDay.getDate() ? d : prev.firstDay,
            lastDay: !prev || d.getDate() > prev.lastDay.getDate() ? d : prev.lastDay,
        });
    });

    return Array.from(months.entries()).map(([month, {firstDay, lastDay}]) => {
        const firstDate = 1;
        const lastDate = withLastMonthDay(lastDay).getDate();

        return {
            month,
            firstDay,
            lastDay,
            // Check if the date is either 1st of month or a Monday within first 3 days of month
            fromStart: firstDay.getDate() === firstDate || (firstDay.getDay() === WeekDay.Monday && firstDay.getDate() <= firstDate + 2),
            // Check if the date is either last day of month or a Friday within last 2 days of month
            toEnd: lastDay.getDate() === lastDate || (lastDay.getDay() === WeekDay.Friday && lastDay.getDate() >= lastDate - 2)
        };
    })
}

export function defaultEmployeeVisitReportName(date: Date = new Date()): string {
    return `employee_visits_${date.toISOString()}.csv`;
}

function normalizeCsvReportName(reportName: string): string {
    const trimmed = reportName.trim();
    const fallback = trimmed || defaultEmployeeVisitReportName();
    return fallback.endsWith('.csv') ? fallback : `${fallback}.csv`;
}

export function downloadEmployeeVisitCsv(employeeMap: EmployeeMap, sheet: EmployeeVisitSheet, reportName = defaultEmployeeVisitReportName()) {
    const workDayIndices = sheet.dates
        .map((date, index) => ({date, index}))
        .filter(({date}) => !isWeekend(date))
        .map(({index}) => index);
    const model = new CsvModel<OfficeVisitSheetRow<Employee, EmployeeVisitType>>([
            {
                header: 'Employee',
                value: row => row.value.fullName,
            },
            {
                header: 'Employee ID',
                value: row => row.value.hrEmployeeId ?? '',
            },
            {
                header: 'Email',
                value: row => row.value.email,
            },
            {
                header: 'HiBob URL',
                value: row => {
                    return row.value.hibobId ? hibobEmployeeUrl(row.value) : '<missing>';
                },
            },
            ...sheet.dates
                .map((date, dateIndex) => ({
                    header: date,
                    value: (row: OfficeVisitSheetRow<EmployeeRef, EmployeeVisitType>): string =>
                        isEmployeeVisit(row.visits[dateIndex]) ? '1' : '0'
                })),
            ...reportMonths(sheet)
                .map(({month, firstDay, lastDay, fromStart, toEnd}) => ({
                    header: fromStart && toEnd ? month : `${isoDate(firstDay)}–${isoDate(lastDay)}`,
                    value: (row: OfficeVisitSheetRow<EmployeeRef, EmployeeVisitType>): string => {
                        const first = isoDate(firstDay);
                        const last = isoDate(lastDay);
                        return sheet.dates
                            .reduce((count, date, dateIndex) => {
                                return count + (
                                    first <= date && date <= last && isEmployeeVisit(row.visits[dateIndex]) ? 1 : 0
                                );
                            }, 0)
                            .toString();
                    },
                })),
            {
                header: 'Total',
                value: row => row.visits
                    .reduce((count, visit) => count + (isEmployeeVisit(visit) ? 1 : 0), 0)
                    .toString(),
            },
            {
                header: 'Total %',
                value: row => {
                    const eligibleWorkDayIndices = workDayIndices.filter(
                        index => row.visits[index] !== EmployeeVisitType.BeforeStart
                    );
                    const workDayVisitCount = eligibleWorkDayIndices.filter(
                        index => row.visits[index] === EmployeeVisitType.WorkDayVisit
                    ).length;
                    return eligibleWorkDayIndices.length === 0
                        ? '—'
                        : `${Math.round(workDayVisitCount / eligibleWorkDayIndices.length * 100)}%`;
                },
            },
        ])
    ;

    model.downloadCsv(
        normalizeCsvReportName(reportName),
        sheet.rows.map(row => ({
            value: employeeMap.get(row.value) || {...emptyEmployee, ...row.value},
            visits: row.visits,
        })),
    );
}
